import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Target, Heart, Users, Zap } from 'lucide-react';

export const About = () => {
  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Building the Future of
            <span className="block mt-2 bg-gradient-to-r from-purple-600 via-pink-500 to-blue-600 bg-clip-text text-transparent">
              Voice AI Communication
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Nyhive was born from a vision: to make advanced Voice AI accessible to every business, 
            powered by the world's most sophisticated AI technology.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Our Story</h2>
          <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
            <p>
              Founded in December 2025, Nyhive emerged from a clear vision: to democratize access to advanced 
              Voice AI technology for businesses of all sizes. We recognized that while Voice AI was the future 
              of customer interaction, existing solutions were either prohibitively expensive or lacked the 
              natural quality that modern users expect.
            </p>
            <p>
              Built on OpenAI's industry-leading real-time APIs, Nyhive delivers enterprise-grade voice experiences 
              at startup-friendly pricing. We're on a mission to make intelligent, natural voice communication 
              accessible to every business - from ambitious startups to established enterprises.
            </p>
            <p>
              As we grow, our commitment remains unchanged: provide the best Voice AI platform that combines 
              cutting-edge technology, exceptional quality, and accessible pricing. We're just getting started, 
              and we're excited to help businesses transform how they communicate with their customers.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-16">Our Values</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="text-purple-600" size={32} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Innovation-Driven</h3>
                <p className="text-gray-600">We leverage cutting-edge AI technology to deliver the best voice experiences.</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="text-pink-600" size={32} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Customer-Centric</h3>
                <p className="text-gray-600">Every feature we build starts with understanding our customers' needs.</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="text-blue-600" size={32} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Accessible</h3>
                <p className="text-gray-600">We believe advanced Voice AI should be accessible to businesses of all sizes.</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="text-purple-600" size={32} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Excellence</h3>
                <p className="text-gray-600">We constantly push boundaries to deliver industry-leading quality.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">Leadership Team</h2>
          <p className="text-center text-xl text-gray-600 mb-16">Passionate experts building the future of Voice AI</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-purple-600 via-pink-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  AS
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1">S Allwin Richard Simon</h3>
                <p className="text-purple-600 font-medium mb-2">Founder & CEO</p>
                <p className="text-sm text-gray-600">Visionary leader driving Voice AI innovation</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-purple-600 via-pink-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  VS
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1">Vishal Sharma</h3>
                <p className="text-purple-600 font-medium mb-2">Customer Experience Head</p>
                <p className="text-sm text-gray-600">Ensuring exceptional customer satisfaction</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-purple-600 via-pink-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  PR
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1">Prabhuraj</h3>
                <p className="text-purple-600 font-medium mb-2">Sales and Growth Head</p>
                <p className="text-sm text-gray-600">Driving business growth and partnerships</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-purple-600 via-pink-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  VK
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1">Vashisht Kumar</h3>
                <p className="text-purple-600 font-medium mb-2">Finance & Partnership Strategist</p>
                <p className="text-sm text-gray-600">Strategic financial planning and partnerships</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-purple-600 via-pink-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                  YG
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1">Yamini Gaur</h3>
                <p className="text-purple-600 font-medium mb-2">Marketing & Sales VP</p>
                <p className="text-sm text-gray-600">Leading marketing and sales excellence</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-gradient-to-br from-purple-600 via-pink-600 to-blue-600 text-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16">By the Numbers</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold mb-2">50ms</div>
              <div className="text-purple-100">Response Latency</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">50+</div>
              <div className="text-purple-100">Languages</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">10M+</div>
              <div className="text-purple-100">Voice Interactions</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">99.9%</div>
              <div className="text-purple-100">Uptime SLA</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};